<script setup>
import { computed } from 'vue'
import { useI18n } from 'vue-i18n'
import { formatDate } from '@/lib/dates'
import NewsIcon from '@/components/NewsIcon.vue'
import NewsText from '@/components/NewsText.vue'

/**
 * The news of one dialog (see doc/news.md): the full text of the news that was opened, from the play
 * page column or from NewsView. The news has no address of its own, so this dialog is the only place
 * it is read. The head says what kind of news it is and when; the text is the same component the
 * lists use, so the names are links here too.
 */
const props = defineProps({
  news: { type: Object, default: null },
})

const emit = defineEmits(['close'])

const { t } = useI18n()

// A manual news may lead somewhere: an absolute URL, or the name of a route of the site (see
// doc/news.md — an internal address is stored as a route, never as a hand-written path).
const externalLink = computed(() => (props.news?.link?.startsWith('http') ? props.news.link : null))
const routeLink = computed(() => (props.news?.link && !externalLink.value
  ? { name: props.news.link, params: props.news.linkParams ?? {} }
  : null))
</script>

<template>
  <v-dialog
    :model-value="!!news"
    max-width="600"
    @update:model-value="value => { if (!value) emit('close') }"
  >
    <v-card v-if="news">
      <div class="news-head">
        <NewsIcon :news="news" :size="22" boxed />

        <div class="news-head__text">
          <div class="news-head__kind">{{ t(`news.categories.${news.category}`) }}</div>
          <div v-if="!news.rule" class="news-head__title">{{ news.title }}</div>
          <div class="news-head__date">{{ formatDate(news.publishedAt) }}</div>
        </div>

        <v-spacer />
        <v-btn
          icon="mdi-close"
          size="small"
          variant="text"
          :title="t('common.close')"
          @click="emit('close')"
        />
      </div>

      <v-divider />

      <v-card-text class="news-body">
        <NewsText :news="news" body />
      </v-card-text>

      <v-card-actions>
        <v-btn
          v-if="externalLink"
          :href="externalLink"
          target="_blank"
          rel="noopener"
          color="primary"
          variant="tonal"
          prepend-icon="mdi-open-in-new"
        >{{ t('news.learnMore') }}</v-btn>
        <v-btn
          v-else-if="routeLink"
          :to="routeLink"
          color="primary"
          variant="tonal"
          prepend-icon="mdi-arrow-right"
        >{{ t('news.learnMore') }}</v-btn>

        <v-spacer />
        <v-btn color="primary" variant="flat" @click="emit('close')">{{ t('common.close') }}</v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<style scoped>
.news-head {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 16px;
}

.news-head__text {
  min-width: 0;
}

.news-head__kind {
  font-size: .72rem;
  font-weight: 600;
  letter-spacing: .08em;
  text-transform: uppercase;
  color: rgba(var(--v-theme-on-surface), .6);
}

.news-head__title {
  font-size: 1.05rem;
  font-weight: 600;
  line-height: 1.3;
}

.news-head__date {
  font-size: .82rem;
  color: rgba(var(--v-theme-on-surface), .6);
}

.news-body {
  font-size: 1rem;
  line-height: 1.6;
  white-space: pre-line;
}
</style>
